import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { AuthContext } from "../helpers/AuthContext";
import { loadConfig } from "../helpers/config";

function Admin() {
    const { authState } = useContext(AuthContext);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [newUserPassword, setNewUserPassword] = useState("");
    const [apiKeys, setApiKeys] = useState([]);
    const [newApiKey, setNewApiKey] = useState(null);
    const [apiUrl, setApiUrl] = useState("");
    const [loadingConfig, setLoadingConfig] = useState(true);

    // Permission groups state
    const [permissionGroups, setPermissionGroups] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allToolsGroups, setAllToolsGroups] = useState([]);
    const [editingGroup, setEditingGroup] = useState(null); // null = create mode, object = edit mode
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [groupFormData, setGroupFormData] = useState({ name: "", description: "", userIds: [], toolsGroupIds: [], canAccessLicenses: false });
    const groupFormRef = useRef(null);


    // Check password requirements
    const checkPasswordRequirements = (password) => {
        return {
            minLength: password.length >= 8,
            hasLowercase: /[a-z]/.test(password),
            hasUppercase: /[A-Z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecial: /[^a-zA-Z0-9]/.test(password),
        };
    };

    const passwordRequirements = checkPasswordRequirements(newUserPassword);

    const initialValues = {
        username: "",
        password: "",
        isAdmin: false,
    };

    const validationSchema = Yup.object().shape({
        username: Yup.string().min(3).max(15).required(),
        password: Yup.string()
            .min(8, "Passwort muss mindestens 8 Zeichen lang sein")
            .matches(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
            .matches(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
            .matches(/[0-9]/, "Passwort muss mindestens eine Ziffer enthalten")
            .matches(/[^a-zA-Z0-9]/, "Passwort muss mindestens ein Sonderzeichen enthalten")
            .required("Passwort ist erforderlich"),
    });

    const onSubmit = (data, { resetForm }) => {
        axios.post(`${apiUrl}/admin/new-user`, data, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            if (response.data.error) {
                setErrorMessage(response.data.error);
                setSuccessMessage("");
            } else {
                setSuccessMessage(response.data.success);
                setErrorMessage("");
                setNewUserPassword("");
                resetForm();
            }
        });
    };

    // API Key Management
    const apiKeyInitialValues = {
        name: "",
        description: "",
    };

    const apiKeyValidationSchema = Yup.object().shape({
        name: Yup.string().min(3).max(50).required("Name ist erforderlich"),
    });

    const fetchApiKeys = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/admin/api-keys`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            setApiKeys(response.data);
        }).catch((error) => {
            console.error("Error fetching API keys:", error);
        });
    }, [apiUrl]);

    const fetchPermissionGroups = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/admin/permission-groups`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((res) => setPermissionGroups(res.data))
            .catch((err) => console.error("Error fetching permission groups:", err));
    }, [apiUrl]);

    const fetchAllUsers = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/admin/users`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((res) => setAllUsers(res.data))
            .catch((err) => console.error("Error fetching users:", err));
    }, [apiUrl]);

    const fetchAllToolsGroups = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/toolsgroups`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((res) => setAllToolsGroups(res.data))
            .catch((err) => console.error("Error fetching toolsgroups:", err));
    }, [apiUrl]);

    useEffect(() => {
        async function fetchConfig() {
            const config = await loadConfig();
            setApiUrl(config.APIURL);
            setLoadingConfig(false);
        }
        fetchConfig();
    }, []);

    useEffect(() => {
        if (authState.isAdmin && apiUrl) {
            fetchApiKeys();
            fetchPermissionGroups();
            fetchAllUsers();
            fetchAllToolsGroups();
        }
    }, [authState.isAdmin, apiUrl, fetchApiKeys, fetchPermissionGroups, fetchAllUsers, fetchAllToolsGroups]);

    const onApiKeySubmit = (data, { resetForm }) => {
        axios.post(`${apiUrl}/admin/api-keys`, data, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            if (response.data.error) {
                setErrorMessage(response.data.error);
                setSuccessMessage("");
                setNewApiKey(null);
            } else {
                setSuccessMessage(response.data.success);
                setErrorMessage("");
                setNewApiKey(response.data.apiKey);
                resetForm();
                fetchApiKeys();
            }
        }).catch((error) => {
            setErrorMessage("Fehler beim Erstellen des API-Schlüssels");
            setSuccessMessage("");
        });
    };

    const deleteApiKey = (id) => {
        if (!window.confirm("Möchten Sie diesen API-Schlüssel wirklich löschen?")) {
            return;
        }

        axios.delete(`${apiUrl}/admin/api-keys/${id}`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            setSuccessMessage(response.data.success);
            setErrorMessage("");
            fetchApiKeys();
        }).catch((error) => {
            setErrorMessage("Fehler beim Löschen des API-Schlüssels");
            setSuccessMessage("");
        });
    };

    const toggleApiKey = (id) => {
        axios.patch(`${apiUrl}/admin/api-keys/${id}/toggle`, {}, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            setSuccessMessage(response.data.success);
            setErrorMessage("");
            fetchApiKeys();
        }).catch((error) => {
            setErrorMessage("Fehler beim Ändern des Status");
            setSuccessMessage("");
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert("API-Schlüssel in die Zwischenablage kopiert!");
        });
    };

    // ─── Permission group handlers ────────────────────────────────────────────
    const scrollToForm = () => {
        setTimeout(() => {
            groupFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    const openCreateGroup = () => {
        setEditingGroup(null);
        setGroupFormData({ name: "", description: "", userIds: [], toolsGroupIds: [], canAccessLicenses: false });
        setShowGroupForm(true);
        scrollToForm();
    };

    const openEditGroup = (group) => {
        setEditingGroup(group);
        setGroupFormData({
            name: group.name,
            description: group.description || "",
            userIds: group.Users.map((u) => u.id),
            toolsGroupIds: group.ToolsGroups.map((tg) => tg.id),
            canAccessLicenses: group.canAccessLicenses || false,
        });
        setShowGroupForm(true);
        scrollToForm();
    };

    const saveGroup = () => {
        const headers = { accessToken: localStorage.getItem("accessToken") };
        const payload = { ...groupFormData };
        const request = editingGroup
            ? axios.put(`${apiUrl}/admin/permission-groups/${editingGroup.id}`, payload, { headers })
            : axios.post(`${apiUrl}/admin/permission-groups`, payload, { headers });

        request.then(() => {
            setShowGroupForm(false);
            setEditingGroup(null);
            fetchPermissionGroups();
            setSuccessMessage(editingGroup ? "Benutzergruppe aktualisiert" : "Benutzergruppe erstellt");
            setErrorMessage("");
        }).catch(() => {
            setErrorMessage("Fehler beim Speichern der Benutzergruppe");
            setSuccessMessage("");
        });
    };

    const deleteGroup = (id) => {
        if (!window.confirm("Benutzergruppe wirklich löschen?")) return;
        axios.delete(`${apiUrl}/admin/permission-groups/${id}`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then(() => {
            fetchPermissionGroups();
            setSuccessMessage("Benutzergruppe gelöscht");
            setErrorMessage("");
        }).catch(() => {
            setErrorMessage("Fehler beim Löschen der Benutzergruppe");
            setSuccessMessage("");
        });
    };

    const toggleGroupId = (id, field) => {
        setGroupFormData((prev) => {
            const arr = prev[field];
            return {
                ...prev,
                [field]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
            };
        });
    };

    const [activeTab, setActiveTab] = useState("users");

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setErrorMessage("");
        setSuccessMessage("");
    };

    if (!authState.isAdmin) {
        return (
            <div className="container" style={{ marginTop: '100px', textAlign: 'center' }}>
                <h2>⚠️ Zugriff verweigert</h2>
                <p>Sie benötigen Administrator-Rechte für diesen Bereich.</p>
            </div>
        );
    }

    if (loadingConfig) {
        return (
            <div className="container" style={{ marginTop: '100px', textAlign: 'center' }}>
                <h2>Lade Konfiguration...</h2>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>🛡️ Admin-Bereich</h1>
                <p>Verwaltung von Benutzern, API-Schlüsseln und Benutzergruppen</p>
            </div>

            {/* Sub-Menu Tabs */}
            <div className="admin-submenu">
                <button
                    className={`admin-tab ${activeTab === "users" ? "admin-tab-active" : ""}`}
                    onClick={() => handleTabChange("users")}
                >
                    👤 Benutzer
                </button>
                <button
                    className={`admin-tab ${activeTab === "apikeys" ? "admin-tab-active" : ""}`}
                    onClick={() => handleTabChange("apikeys")}
                >
                    🔑 API-Schlüssel
                </button>
                <button
                    className={`admin-tab ${activeTab === "groups" ? "admin-tab-active" : ""}`}
                    onClick={() => handleTabChange("groups")}
                >
                    👥 Benutzergruppen
                </button>
            </div>

            <div className="admin-content">
                {/* Success/Error Messages */}
                {errorMessage && (
                    <div className="alert alert-error">
                        <strong>❌ Fehler:</strong> {errorMessage}
                    </div>
                )}
                {successMessage && (
                    <div className="alert alert-success">
                        <strong>✓ Erfolg:</strong> {successMessage}
                    </div>
                )}

                {/* User Creation Section */}
                {activeTab === "users" && <div className="admin-section">
                    <div className="section-header">
                        <h2>👤 Neuen Benutzer anlegen</h2>
                        <p className="section-description">
                            Erstellen Sie neue Benutzerkonten für das System
                        </p>
                    </div>

                    <div className="form-card">
                        <Formik
                            initialValues={initialValues}
                            onSubmit={onSubmit}
                            validationSchema={validationSchema}
                        >
                            {({ setFieldValue }) => (
                                <Form>
                                    <div className="form-group">
                                        <label>Benutzername:</label>
                                        <ErrorMessage name="username" component="span" className="error-message" />
                                        <Field
                                            autoComplete="off"
                                            name="username"
                                            placeholder="z.B. max.mustermann"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Passwort:</label>
                                        <ErrorMessage name="password" component="span" className="error-message" />
                                        <Field
                                            autoComplete="off"
                                            type="password"
                                            name="password"
                                            placeholder="Sicheres Passwort"
                                            className="form-control"
                                            onChange={(e) => {
                                                setFieldValue('password', e.target.value);
                                                setNewUserPassword(e.target.value);
                                            }}
                                        />

                                        {newUserPassword && (
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '15px',
                                                background: '#f8f9fa',
                                                borderRadius: '6px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                <strong style={{ display: 'block', marginBottom: '10px', color: '#495057' }}>🔒 Passwort-Stärke:</strong>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.minLength ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.minLength ? '✔' : '✖'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.minLength ? '#28a745' : '#6c757d' }}>
                                                            Mindestens 8 Zeichen
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasLowercase ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasLowercase ? '✔' : '✖'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasLowercase ? '#28a745' : '#6c757d' }}>
                                                            Mindestens ein Kleinbuchstabe (a-z)
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasUppercase ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasUppercase ? '✔' : '✖'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasUppercase ? '#28a745' : '#6c757d' }}>
                                                            Mindestens ein Großbuchstabe (A-Z)
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasNumber ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasNumber ? '✔' : '✖'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasNumber ? '#28a745' : '#6c757d' }}>
                                                            Mindestens eine Ziffer (0-9)
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasSpecial ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasSpecial ? '✔' : '✖'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasSpecial ? '#28a745' : '#6c757d' }}>
                                                            Mindestens ein Sonderzeichen (!@#$%^&*)
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label className="checkbox-label">
                                            <Field type="checkbox" name="isAdmin" />
                                            <span>Administrator-Rechte erteilen</span>
                                        </label>
                                    </div>

                                    <button type="submit" className="btn btn-primary">
                                        ➕ Benutzer anlegen
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>}

                {/* API Key Management Section */}
                {activeTab === "apikeys" && <div className="admin-section">
                    <div className="section-header">
                        <h2>🔑 API-Schlüssel für Backups</h2>
                        <p className="section-description">
                            API-Schlüssel ermöglichen automatisierte Backups ohne Login.
                            Verwenden Sie diese für Cron-Jobs oder Backup-Skripte.
                        </p>
                    </div>

                    <div className="form-card">
                        <Formik
                            initialValues={apiKeyInitialValues}
                            onSubmit={onApiKeySubmit}
                            validationSchema={apiKeyValidationSchema}
                        >
                            <Form>
                                <div className="form-group">
                                    <label>Name des Schlüssels:</label>
                                    <ErrorMessage name="name" component="span" className="error-message" />
                                    <Field
                                        autoComplete="off"
                                        name="name"
                                        placeholder="z.B. Backup Server 1"
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Beschreibung (optional):</label>
                                    <Field
                                        autoComplete="off"
                                        name="description"
                                        placeholder="z.B. Tägliche Backups auf Server XYZ"
                                        className="form-control"
                                        as="textarea"
                                        rows="2"
                                    />
                                </div>

                                <button type="submit" className="btn btn-primary">
                                    ➕ API-Schlüssel erstellen
                                </button>
                            </Form>
                        </Formik>
                    </div>

                    {newApiKey && (
                        <div className="api-key-display">
                            <div className="api-key-warning">
                                <h3>⚠️ Wichtig: Speichern Sie diesen Schlüssel!</h3>
                                <p>Dieser API-Schlüssel wird nur einmal angezeigt. Speichern Sie ihn an einem sicheren Ort.</p>
                            </div>
                            <div className="api-key-details">
                                <div className="detail-row">
                                    <strong>Name:</strong> {newApiKey.name}
                                </div>
                                {newApiKey.description && (
                                    <div className="detail-row">
                                        <strong>Beschreibung:</strong> {newApiKey.description}
                                    </div>
                                )}
                            </div>
                            <div className="api-key-value">
                                <label>Ihr API-Schlüssel:</label>
                                <div className="key-display">{newApiKey.key}</div>
                            </div>
                            <div className="api-key-actions">
                                <button
                                    onClick={() => copyToClipboard(newApiKey.key)}
                                    className="btn btn-success"
                                >
                                    📋 In Zwischenablage kopieren
                                </button>
                                <button
                                    onClick={() => setNewApiKey(null)}
                                    className="btn btn-secondary"
                                >
                                    Schließen
                                </button>
                            </div>
                        </div>
                    )}

                    {/* API Keys List */}
                    <div className="api-keys-list">
                        <h3>Vorhandene API-Schlüssel</h3>
                        {apiKeys.length === 0 ? (
                            <div className="empty-state">
                                <p>🔑 Keine API-Schlüssel vorhanden.</p>
                                <p className="text-muted">Erstellen Sie Ihren ersten API-Schlüssel oben.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="api-keys-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Beschreibung</th>
                                            <th>Erstellt von</th>
                                            <th>Erstellt am</th>
                                            <th>Zuletzt verwendet</th>
                                            <th>Status</th>
                                            <th>Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {apiKeys.map((key) => (
                                            <tr key={key.id}>
                                                <td><strong>{key.name}</strong></td>
                                                <td className="text-muted">{key.description || '-'}</td>
                                                <td>{key.creator.username}</td>
                                                <td>{new Date(key.createdAt).toLocaleDateString('de-DE')}</td>
                                                <td>
                                                    {key.lastUsed
                                                        ? new Date(key.lastUsed).toLocaleString('de-DE', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })
                                                        : <span className="text-muted">Nie</span>
                                                    }
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${key.isActive ? 'status-active' : 'status-inactive'}`}>
                                                        {key.isActive ? '● Aktiv' : '○ Inaktiv'}
                                                    </span>
                                                </td>
                                                <td className="actions-cell">
                                                    <button
                                                        onClick={() => toggleApiKey(key.id)}
                                                        className="btn btn-small btn-secondary"
                                                        title={key.isActive ? 'Deaktivieren' : 'Aktivieren'}
                                                    >
                                                        {key.isActive ? '⏸' : '▶'}
                                                    </button>
                                                    <button
                                                        onClick={() => deleteApiKey(key.id)}
                                                        className="btn btn-small btn-danger"
                                                        title="Löschen"
                                                    >
                                                        🗑️
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>}

                {/* Permission Groups Section */}
                {activeTab === "groups" && <div className="admin-section">
                    <div className="section-header">
                        <h2>👥 Benutzergruppen &amp; Berechtigungen</h2>
                        <p className="section-description">
                            Schränken Sie ein, welche Werkzeuggruppen ein Benutzer sehen und prüfen darf.
                            Benutzer ohne Gruppenzuordnung haben vollen Zugriff.
                        </p>
                    </div>

                    <div className="form-card" ref={groupFormRef}>
                        {!showGroupForm ? (
                            <button className="btn btn-primary" onClick={openCreateGroup}>
                                ➕ Neue Benutzergruppe
                            </button>
                        ) : (
                            <>
                                <h3>{editingGroup ? "Benutzergruppe bearbeiten" : "Neue Benutzergruppe"}</h3>

                                <div className="form-group">
                                    <label>Name:</label>
                                    <input
                                        className="form-control"
                                        value={groupFormData.name}
                                        onChange={(e) => setGroupFormData((p) => ({ ...p, name: e.target.value }))}
                                        placeholder="z.B. Techniker Halle A"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Beschreibung (optional):</label>
                                    <input
                                        className="form-control"
                                        value={groupFormData.description}
                                        onChange={(e) => setGroupFormData((p) => ({ ...p, description: e.target.value }))}
                                        placeholder="Kurze Beschreibung"
                                    />
                                </div>

                                <div className="form-group">
                                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>Benutzer in dieser Gruppe:</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                                        {allUsers.map((u) => (
                                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', width: 'fit-content' }}>
                                                <span style={{ width: '15ch', flexShrink: 0 }}>{u.username}{u.fullName ? ` (${u.fullName})` : ""}{u.isAdmin ? " 🛡️" : ""}</span>
                                                <input
                                                    type="checkbox"
                                                    style={{ flexShrink: 0 }}
                                                    checked={groupFormData.userIds.includes(u.id)}
                                                    onChange={() => toggleGroupId(u.id, 'userIds')}
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>Berechtigungen:</div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', width: 'fit-content', marginTop: '6px' }}>
                                        <input
                                            type="checkbox"
                                            checked={groupFormData.canAccessLicenses}
                                            onChange={(e) => setGroupFormData((p) => ({ ...p, canAccessLicenses: e.target.checked }))}
                                        />
                                        <span>Führerscheinkontrolle einsehen &amp; prüfen</span>
                                    </label>
                                </div>

                                <div className="form-group">
                                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>Zugelassene Werkzeuggruppen:</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                                        {allToolsGroups.map((tg) => (
                                            <label key={tg.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', width: 'fit-content' }}>
                                                <span style={{ width: '20ch', flexShrink: 0 }}>{tg.groupname}</span>
                                                <input
                                                    type="checkbox"
                                                    style={{ flexShrink: 0 }}
                                                    checked={groupFormData.toolsGroupIds.includes(tg.id)}
                                                    onChange={() => toggleGroupId(tg.id, 'toolsGroupIds')}
                                                />
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button className="btn btn-primary" onClick={saveGroup}>
                                        💾 Speichern
                                    </button>
                                    <button className="btn btn-secondary" onClick={() => setShowGroupForm(false)}>
                                        Abbrechen
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {!showGroupForm && (permissionGroups.length === 0 ? (
                        <div className="empty-state">
                            <p>👥 Keine Benutzergruppen vorhanden.</p>
                            <p className="text-muted">Alle Benutzer haben derzeit vollen Zugriff.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="api-keys-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Beschreibung</th>
                                        <th>Benutzer</th>
                                        <th>Werkzeuggruppen</th>
                                        <th>Führerscheine</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {permissionGroups.map((group) => (
                                        <tr key={group.id}>
                                            <td><strong>{group.name}</strong></td>
                                            <td className="text-muted">{group.description || '-'}</td>
                                            <td>
                                                {group.Users.length === 0
                                                    ? <span className="text-muted">–</span>
                                                    : group.Users.map((u, i) => (
                                                        <span key={u.id}>{u.username}{i < group.Users.length - 1 && <br />}</span>
                                                    ))}
                                            </td>
                                            <td>
                                                {group.ToolsGroups.length === 0
                                                    ? <span className="text-muted" title="Benutzer in dieser Gruppe können nichts sehen">⚠️ Keine</span>
                                                    : group.ToolsGroups.map((tg) => tg.groupname).join(', ')}
                                            </td>
                                            <td>
                                                {group.canAccessLicenses
                                                    ? <span className="status-badge status-active">✓ Ja</span>
                                                    : <span className="text-muted">–</span>}
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="btn btn-small btn-secondary"
                                                    onClick={() => openEditGroup(group)}
                                                    title="Bearbeiten"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="btn btn-small btn-danger"
                                                    onClick={() => deleteGroup(group.id)}
                                                    title="Löschen"
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>}
            </div>
        </div>
    );
}

export default Admin;
